import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Search,
  Grid3X3,
  List,
  ChevronDown,
  ChevronUp,
  Sparkles,
  FileX,
} from 'lucide-react'
import { PresetCard } from './PresetCard'
import { PresetPreviewDialog } from './PresetPreviewDialog'
import { PresetApplicationProgress } from './PresetApplicationProgress'
import { cn } from '@/lib/utils'
import type {
  IndustryPresetSummary,
  IndustryPreset,
  PresetApplicationResult,
  IndustryType,
  PresetComplexity,
} from '@/lib/api/services/industryPresets'
import { industryPresetsService } from '@/lib/api/services/industryPresets'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

interface PresetSelectorPageProps {
  onPresetApplied?: (result: PresetApplicationResult) => void
  showHeader?: boolean
  className?: string
}

const industryOptions: { value: IndustryType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Industries' },
  { value: 'Legal', label: 'Legal' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Technology', label: 'Technology' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Education', label: 'Education' },
  { value: 'Consulting', label: 'Consulting' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'RealEstate', label: 'Real Estate' },
  { value: 'Insurance', label: 'Insurance' },
  { value: 'HumanResources', label: 'Human Resources' },
  { value: 'Government', label: 'Government' },
  { value: 'NonProfit', label: 'Non-Profit' },
]

const complexityOptions: { value: PresetComplexity | 'all'; label: string }[] = [
  { value: 'all', label: 'All Complexities' },
  { value: 'Basic', label: 'Basic' },
  { value: 'Standard', label: 'Standard' },
  { value: 'Advanced', label: 'Advanced' },
  { value: 'Enterprise', label: 'Enterprise' },
]

export function PresetSelectorPage({
  onPresetApplied,
  showHeader = true,
  className,
}: PresetSelectorPageProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // State
  const [searchQuery, setSearchQuery] = useState('')
  const [industryFilter, setIndustryFilter] = useState<IndustryType | 'all'>('all')
  const [complexityFilter, setComplexityFilter] = useState<PresetComplexity | 'all'>('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showAllPresets, setShowAllPresets] = useState(false)

  // Preview dialog state
  const [previewPreset, setPreviewPreset] = useState<IndustryPreset | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  // Application progress state
  const [applyingPreset, setApplyingPreset] = useState<{
    name: string
    color: string
  } | null>(null)
  const [applicationResult, setApplicationResult] = useState<PresetApplicationResult | null>(null)
  const [applicationError, setApplicationError] = useState<string | null>(null)

  // Fetch all presets
  const { data: presets, isLoading } = useQuery({
    queryKey: ['industryPresets'],
    queryFn: () => industryPresetsService.getAll(true),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Apply preset mutation
  const applyPresetMutation = useMutation({
    mutationFn: async ({
      presetId,
    }: {
      presetId: string
      includeDefaultAgents: boolean
    }) => {
      return industryPresetsService.apply(presetId, {
        skipExisting: true,
        applyBrandSettings: false,
      })
    },
    onSuccess: (result) => {
      setApplicationResult(result)
      queryClient.invalidateQueries({ queryKey: ['agents'] })
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      onPresetApplied?.(result)
    },
    onError: (error: Error) => {
      setApplicationError(error.message)
    },
  })

  // Filter presets
  const filteredPresets = useMemo(() => {
    if (!presets) return []

    return presets.filter((preset) => {
      const matchesSearch =
        !searchQuery ||
        preset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        preset.description.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesIndustry = industryFilter === 'all' || preset.industry === industryFilter
      const matchesComplexity = complexityFilter === 'all' || preset.complexity === complexityFilter

      return matchesSearch && matchesIndustry && matchesComplexity
    })
  }, [presets, searchQuery, industryFilter, complexityFilter])

  // Separate featured and regular presets
  const featuredPresets = filteredPresets.filter((p) => p.isFeatured)
  const regularPresets = filteredPresets.filter((p) => !p.isFeatured)
  const displayedPresets = showAllPresets ? filteredPresets : featuredPresets.slice(0, 6)

  // Handle preview
  async function handlePreview(preset: IndustryPresetSummary) {
    try {
      const fullPreset = await industryPresetsService.getById(preset.id)
      setPreviewPreset(fullPreset)
      setIsPreviewOpen(true)
    } catch (error) {
      console.error('Failed to load preset preview:', error)
    }
  }

  // Handle selection (direct apply without preview)
  function handleSelect(preset: IndustryPresetSummary) {
    handlePreview(preset)
  }

  // Handle apply from preview dialog
  function handleApply(preset: IndustryPreset, includeDefaultAgents: boolean) {
    setIsPreviewOpen(false)
    setApplyingPreset({ name: preset.name, color: preset.color })
    setApplicationResult(null)
    setApplicationError(null)
    applyPresetMutation.mutate({ presetId: preset.id, includeDefaultAgents })
  }

  // Handle close progress dialog
  function handleCloseProgress() {
    setApplyingPreset(null)
    setApplicationResult(null)
    setApplicationError(null)
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      {showHeader && (
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Choose Your Industry</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Select an industry preset to quickly configure your workspace with specialized AI agents,
            workflows, and templates tailored for your profession.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search presets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 rounded-xl"
          />
        </div>

        <div className="flex gap-2 flex-wrap">
          <Select value={industryFilter} onValueChange={(v) => setIndustryFilter(v as IndustryType | 'all')}>
            <SelectTrigger className="w-[160px] rounded-xl">
              <SelectValue placeholder="Industry" />
            </SelectTrigger>
            <SelectContent>
              {industryOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={complexityFilter} onValueChange={(v) => setComplexityFilter(v as PresetComplexity | 'all')}>
            <SelectTrigger className="w-[160px] rounded-xl">
              <SelectValue placeholder="Complexity" />
            </SelectTrigger>
            <SelectContent>
              {complexityOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex rounded-xl border">
            <Button
              variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
              size="icon"
              className="rounded-r-none"
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="icon"
              className="rounded-l-none"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className={cn(
          'grid gap-4',
          viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'
        )}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-[280px] rounded-xl" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && filteredPresets.length === 0 && (
        <div className="text-center py-12">
          <FileX className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium">No presets found</h3>
          <p className="text-muted-foreground mt-1">
            Try adjusting your search or filters.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              setSearchQuery('')
              setIndustryFilter('all')
              setComplexityFilter('all')
            }}
          >
            Clear filters
          </Button>
        </div>
      )}

      {/* Presets grid */}
      {!isLoading && filteredPresets.length > 0 && (
        <>
          <div
            className={cn(
              'grid gap-4',
              viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'
            )}
          >
            {displayedPresets.map((preset) => (
              <PresetCard
                key={preset.id}
                preset={preset}
                onPreview={handlePreview}
                onSelect={handleSelect}
              />
            ))}
          </div>

          {/* Show more/less button */}
          {regularPresets.length > 0 && (
            <div className="text-center">
              <Button
                variant="outline"
                onClick={() => setShowAllPresets(!showAllPresets)}
                className="rounded-xl"
              >
                {showAllPresets ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-2" />
                    Show Featured Only
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-2" />
                    View All {filteredPresets.length} Presets
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Start from blank option */}
          <div className="text-center pt-4 border-t">
            <p className="text-sm text-muted-foreground mb-2">
              Want to build your own custom setup?
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/ai-studio')}
              className="rounded-xl"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Start from Blank
            </Button>
          </div>
        </>
      )}

      {/* Preview Dialog */}
      <PresetPreviewDialog
        preset={previewPreset}
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        onApply={handleApply}
        isApplying={applyPresetMutation.isPending}
      />

      {/* Application Progress Dialog */}
      {applyingPreset && (
        <PresetApplicationProgress
          presetName={applyingPreset.name}
          presetColor={applyingPreset.color}
          open={!!applyingPreset}
          isApplying={applyPresetMutation.isPending}
          result={applicationResult}
          error={applicationError}
          onClose={handleCloseProgress}
          onGoToAgents={() => {
            handleCloseProgress()
            navigate('/ai-studio')
          }}
        />
      )}
    </div>
  )
}

export default PresetSelectorPage
